import {
  useWSModal,
  WSActions,
  WSAlert,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayerPayeeEngagementStatus,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYMENT_CARDS } from "../../../../query/paymentCards/keys";
import {
  QUERY_PAYOUT_SETTINGS,
  QUERY_PAYOUT_SETTINGS_DEBIT_CARDS,
  QUERY_PAYROLL_SETTINGS
} from "../../../../query/payments/keys";
import {
  useQueryPayoutSettings,
  useQueryPayrollSettings
} from "../../../../query/payments/queries";
import {
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_CLIENT
} from "../../../../query/users/keys";
import { useClientQuery } from "../../../../query/users/queries";
import { useQueryPayeeRows } from "../../../../query/search/payee/queries/useQueryPayeeRows";
import {
  PAYMENT_METHOD_REPLACEMENT_ALERT_BODY,
  PAYMENT_METHOD_REPLACEMENT_ALERT_TITLE
} from "../../../../shared/constants/paymentMethodReplacement";
import { paymentsService } from "../../../../services/payments";
import { usersService } from "../../../../services/users";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD } from "../../paths";
import { useMethodLabelProps } from "../LabelMethod/useMethodLabelProps";
import { MethodType } from "./types";

type Props = {
  onClose: (isRemoved: boolean) => void;
  id: string;
  type: MethodType;
};

export const ModalRemove: React.FC<Props> = props => {
  const userId = useUserId();
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const queryClient = useClientQuery(userId);
  const history = useHistory();

  const isPayrollFundingMethod = useMemo(
    () =>
      queryPayrollSettings.data?.fundingSource?.fundingSourceId === props.id,
    [props.id, queryPayrollSettings.data?.fundingSource?.fundingSourceId]
  );

  const isPaymentMethod = useMemo(
    () =>
      queryClient.data?.profile.defaultPaymentMethod?.accountId === props.id ||
      queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId ===
        props.id,
    [
      props.id,
      queryClient.data?.profile.defaultPaymentMethod?.accountId,
      queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId
    ]
  );

  // Mirrors backend enforcement: a payer with any Active engagement must keep a method on file.
  // Only fetched when removing the default method (others can't orphan it).
  const activeEngagementsQuery = useQueryPayeeRows(
    {
      filter: { "engagements.status": PayerPayeeEngagementStatus.Active },
      page: { size: 1 }
    },
    { enabled: isPaymentMethod }
  );
  const clientLoaded = !!queryClient.data;
  // Stable disabled action until client + policy state are known (no action swap, a11y).
  const isDeterminingRemoval =
    !clientLoaded ||
    (isPaymentMethod && activeEngagementsQuery.isInitialLoading);
  const mustReplaceBeforeRemoval =
    isPaymentMethod && (activeEngagementsQuery.data?.length ?? 0) > 0;

  const isPayoutStandardMethod = useMemo(
    () =>
      queryPayoutSettings.data?.payoutDestinations.find(
        d => d.payoutMethod === PayoutPreferences.Standard
      )?.destinationId === props.id,
    [props.id, queryPayoutSettings.data?.payoutDestinations]
  );

  const isPayoutInstantMethod = useMemo(
    () =>
      queryPayoutSettings.data?.payoutDestinations.find(
        d => d.payoutMethod === PayoutPreferences.Instant
      )?.destinationId === props.id,
    [props.id, queryPayoutSettings.data?.payoutDestinations]
  );

  const usage = useMemo(() => {
    const list: string[] = [];
    if (isPaymentMethod) {
      list.push("payment method");
    }

    if (isPayoutInstantMethod) {
      list.push("instant payout method");
    }

    if (isPayoutStandardMethod) {
      list.push("standard payout method");
    }

    if (isPayrollFundingMethod) {
      list.push("payroll funding method");
    }

    return list;
  }, [
    isPaymentMethod,
    isPayoutInstantMethod,
    isPayoutStandardMethod,
    isPayrollFundingMethod
  ]);

  const [mutate, meta] = useWSMutation<
    void,
    WSServiceError,
    {
      isPaymentMethod?: boolean;
      isPayoutStandardMethod?: boolean;
      isPayoutInstantMethod?: boolean;
      isPayrollFundingMethod?: boolean;
    }
  >(
    async () => {
      if (isPayoutStandardMethod) {
        await paymentsService.payoutSettings.update(userId, {
          standard: null
        });
      }

      if (isPayoutInstantMethod) {
        await paymentsService.payoutSettings.update(userId, {
          instant: null
        });
      }

      if (isPayrollFundingMethod) {
        await paymentsService.payrollSettings.update(userId, {
          fundingSource: null
        });
      }

      // Clear the default in the same (cards) / an earlier (non-cards) update so a rejection can't orphan it.
      if (props.type === "CreditCard") {
        const client = await usersService.client.get(userId);
        await usersService.client.update(userId, {
          clientId: userId,
          profile: {
            savedPaymentMethods: (client.profile.savedPaymentMethods || []).map(
              pm => (pm.paymentMethodId === props.id ? null : {})
            ),
            ...(isPaymentMethod
              ? {
                  defaultPaymentMethod: {
                    accountId: null,
                    paymentMethodId: null
                  }
                }
              : {})
          }
        });
      } else {
        if (isPaymentMethod) {
          await usersService.client.update(userId, {
            clientId: userId,
            profile: {
              defaultPaymentMethod: {
                accountId: null,
                paymentMethodId: null
              }
            }
          });
        }

        switch (props.type) {
          case "Account":
            await usersService.account.delete(props.id);

            break;
          case "CheckbookCard":
            await paymentsService.payoutSettings.debitCard.delete(
              userId,
              props.id
            );

            break;
          case "PaymentCard":
            await paymentsService.paymentCard.delete(props.id);

            break;
        }
      }
    },
    {
      onSuccess: () => props.onClose(true),
      dependencies: [
        QUERY_USERS_ACCOUNTS,
        QUERY_USERS_CLIENT,
        QUERY_PAYOUT_SETTINGS_DEBIT_CARDS,
        QUERY_PAYMENT_CARDS,
        QUERY_PAYOUT_SETTINGS,
        QUERY_PAYROLL_SETTINGS
      ]
    }
  );

  const { label } = useMethodLabelProps(props.id, props.type);

  return (
    <WSList gap="XL">
      <WSText.ParagraphSm color="gray600">
        Are you sure you want to remove this account?
      </WSText.ParagraphSm>

      <WSCard
        header={{
          label
        }}
      />

      {usage.length > 0 && (
        <WSAlert icon="alert" title="Account in use" theme="warning">
          This bank account is currently used as a {usage.join(", ")}. If you
          remove it, please select another account to set as the default(s).
        </WSAlert>
      )}

      {mustReplaceBeforeRemoval && (
        <WSAlert
          icon="info-circle"
          theme="info"
          title={PAYMENT_METHOD_REPLACEMENT_ALERT_TITLE}
        >
          {PAYMENT_METHOD_REPLACEMENT_ALERT_BODY}
        </WSAlert>
      )}

      <WSErrorMessage contextKey="AddPaymentMethod" error={meta.error} />

      <WSActions
        buttons={
          mustReplaceBeforeRemoval
            ? [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => props.onClose(false)
                },
                {
                  label: "Add payment method",
                  onClick: () => {
                    props.onClose(false);
                    history.push(
                      PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD
                    );
                  }
                }
              ]
            : [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => props.onClose(false)
                },
                {
                  destructive: true,
                  label: "Remove",
                  onClick: () => {
                    mutate({
                      isPaymentMethod,
                      isPayoutStandardMethod,
                      isPayoutInstantMethod,
                      isPayrollFundingMethod
                    });
                  },
                  // Disabled until client + policy state is known.
                  loading: meta.isLoading || isDeterminingRemoval
                }
              ]
        }
      />
    </WSList>
  );
};

export const useModalRemove = () =>
  useWSModal(ModalRemove, {
    title: "Remove account",
    size: "S"
  });
